<template>
  <!-- No content padding: the columns and the footer bar run edge to edge, like the per-sample modal. -->
  <BaseModal
    :modalTitle="$t('W: Beskrivelser til rapporten')"
    contentPadding="none">
    <!-- The modal host leaves 80vh for the whole dialog; its title takes about 6rem of that. -->
    <div class="flex h-[calc(80vh-6rem)] flex-col border-t border-border-subtle">
      <div
        v-if="isLoadingResults"
        class="px-6 py-6 text-sm text-text-muted">
        {{ $t('W: Henter prøver...') }}
      </div>
      <!-- One list for the whole report — every sample gets the same descriptions -->
      <div
        v-else
        class="flex min-h-0 flex-1">
        <ReportDescriptionsPicker
          :selectedItemIDs="selectedItemIDs"
          :suggestedItemIDs="suggestedItemIDs"
          :reportLocale="reportLocale"
          :selectedChipLabel="$t('W: Matcher')"
          :subtitle="$t('W: Gælder alle prøver')"
          @on-change="onSelectionChange" />
      </div>

      <!-- Fixed bottom bar: the buttons, and an error if saving failed -->
      <div class="flex shrink-0 items-center justify-between gap-4 border-t border-border-subtle px-6 py-4">
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <ReportLanguageNotice
            :reportLocale="reportLocale"
            :itemIDs="selectedItemIDs" />
          <p
            v-if="errorText"
            class="text-sm text-feedback-error">
            {{ errorText }}
          </p>
        </div>
        <div class="flex shrink-0 gap-2">
          <UiBaseButton
            :text="$t('action.cancel')"
            theme="neutral"
            @click="$emit('close')" />
          <UiBaseButton
            :text="$t('action.save')"
            :isLoading="isLoading"
            :showSaveIcon="showSaveIcon"
            @click="onConfirm" />
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import type { IAnalysisResult } from '@team-milva/lab-models/resources'
import { useToast } from '@/globals/typescript/utils/toast'
import { fetchOrderAnalysisResults } from '@/globals/typescript/utils/fetchOrderAnalysisResults'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { useModalStore } from '@/stores/modalStore'
import { useOrderStore } from '@/stores/orderStore'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'

/**
 * The descriptions of a standard report: one list for the whole report, the same under every
 * sample. The body is the shared `ReportDescriptionsPicker` — the same as the per-sample modal,
 * without its sample column.
 */
export default {
  name: 'OrderReportDescriptionsModal',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
    tatGroupID: { type: String, required: true },
    reportTemplateID: { type: String, required: true },
    currentItemIDs: { type: Array as () => string[], required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      // Captured synchronously: created() awaits two store loads before it
      // registers, and by then another modal can be the top entry.
      modalEntryID: useModalStore().top?.id,

      selectedItemIDs: [...this.currentItemIDs] as string[],
      isLoadingResults: true,
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
      orderResults: [] as IAnalysisResult[],
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useReportDescriptionStore, ['allReportDescriptions']),
    ...mapState(useReportDescriptionItemStore, ['allReportDescriptionItems']),

    // The language the report is printed in — the order's, as in the preview and the PDF
    reportLocale(): string {
      const order = this.allOrders.find((x) => x.id === this.orderID)
      return order?.reportLocale || this.currentLocale
    },

    // Every reportDescriptionItemID actually picked on this order's own
    // multi-select-text/-number results — a Set, so an item picked across
    // several tests on the same report still only ever counts once.
    pickedItemIDs(): Set<string> {
      const ids = new Set<string>()
      for (const result of this.orderResults) {
        const value = result.result
        if (value.type !== 'multi-select-text' && value.type !== 'multi-select-number') continue
        for (const item of value.items) {
          ids.add(item.reportDescriptionItemID)
        }
      }
      return ids
    },

    // The "Forslag" tab. Only Collections with usesDescriptions on ever contribute a
    // suggestion — an item from a Collection that doesn't use descriptions has nothing
    // meaningful to suggest. Nothing is added by itself: the user ticks what they want.
    // Alphabetical by title, as the "Lister" tab is (MIL-564) — without it they come in whatever
    // order the store returns them. The per-sample modal is deliberately not sorted: its
    // suggestions follow the order of the items in the results table.
    suggestedItemIDs(): string[] {
      if (this.pickedItemIDs.size === 0) return []
      const usesDescriptionsByID = new Map(
        this.allReportDescriptions.map((rd) => [rd.id, rd.options?.usesDescriptions ?? true])
      )
      return this.allReportDescriptionItems
        .filter(
          (item) =>
            this.pickedItemIDs.has(item.id) &&
            item.activeState === 'active' &&
            (usesDescriptionsByID.get(item.reportDescriptionID) ?? true)
        )
        .sort((a, b) =>
          a.getTitle(this.currentLocale).localeCompare(b.getTitle(this.currentLocale))
        )
        .map((item) => item.id)
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['updateOrderReportDescriptionSelections']),

    onSelectionChange(itemIDs: string[]): void {
      this.selectedItemIDs = itemIDs
    },

    hasSelectionChanged(): boolean {
      // Compared as sets on both sides, not by length plus membership. The earlier
      // version could return a false negative: ['a','a'] against a saved ['a','b'] has
      // the same length and every entry is a member, so it read as unchanged even
      // though b had been dropped. Nothing in the UI can produce a duplicate today —
      // ticking checks membership first — but the check should not depend on an
      // invariant it does not enforce. Caught by Copilot on the MIL-291 PR.
      const selected = new Set(this.selectedItemIDs)
      const current = new Set(this.currentItemIDs)
      if (selected.size !== current.size) return true
      return [...selected].some((x) => !current.has(x))
    },

    async onConfirm(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        await this.updateOrderReportDescriptionSelections(
          this.orderID,
          this.tatGroupID,
          this.reportTemplateID,
          this.selectedItemIDs
        )
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        useToast().success(this.$t('W: Beskrivelser gemt'))
        this.$emit('close')
      } catch {
        this.errorText = this.$t('W: Der opstod en fejl')
        useToast().error(this.$t('error.common.standard') as string)
      } finally {
        this.isLoading = false
      }
    },
  },

  async created() {
    // Suggestions are a supplementary layer: the read reports its own failure and hands back
    // an empty list, so the rest of the modal still loads.
    await Promise.all([
      useReportDescriptionStore().getReportDescriptions(),
      useReportDescriptionItemStore().getReportDescriptionItems(),
      fetchOrderAnalysisResults({
        labAccountID: this.labAccount?.id ?? '',
        orderID: this.orderID,
        operation: 'fetch-order-results-for-suggestions',
      }).then((results) => {
        this.orderResults = results
      }),
    ])
    this.isLoadingResults = false

    // `selectedItemIDs` is seeded from the order in data() and committed only by
    // onConfirm, so a changed selection is a draft like any typed one — the
    // audit had this modal down as search/filter state, which covers its
    // search box but not the selection sitting next to it.
    //
    // An explicit isDirty rather than a snapshot: the order of the array is not
    // meaningful (ticking appends, removing filters), so the same set
    // reached in a different order is not an edit and must still close silently.
    // The picker's tab and search are deliberately outside it.
    this.unsavedGuard.register({
      isDirty: () => this.hasSelectionChanged(),
      modalId: this.modalEntryID,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
